import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import moment from "moment-timezone";
import API_BASE_URL from "../config";
import "./ServiciosRegistradosPage.css";
import './SharedLoading.css';
import { ToastContainer, toast } from "react-toastify";
import ModalSolicitarApoyo from "../components/ModalSolicitarApoyo";

const ServiciosRegistradosPage = () => {
  const token = useSelector((state) => state.auth.token);
  const decoded = token ? jwtDecode(token) : null;
  const estacionUsuario = decoded?.estacion?.trim().toUpperCase();

  const [servicios, setServicios] = useState([]);
  const [apoyoServicio, setApoyoServicio] = useState(null); // servicio pasillera para solicitar apoyos
  const [modalVisible, setModalVisible] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
  const [busquedaSiglas, setBusquedaSiglas] = useState("");
  const [busquedaVuelo, setBusquedaVuelo] = useState("");
  const [loadingPage, setLoadingPage] = useState(true);
  const [colaboradoresInfo, setColaboradoresInfo] = useState({});
  const cargaColaboradoresRef = useRef(new Set());
  const [filtroUrgencia, setFiltroUrgencia] = useState("todos");
  const [filtroTipos, setFiltroTipos] = useState({ LLEGADAS: true, SALIDAS: true });
  const [asignando, setAsignando] = useState(false);

  const tiposActivos = useMemo(
    () => Object.entries(filtroTipos).filter(([, activo]) => activo).map(([tipo]) => tipo),
    [filtroTipos]
  );

  const opcionesUrgencia = [
    { valor: "todos", etiqueta: "Todos" },
    { valor: "alta", etiqueta: "Prioridad alta" },
    { valor: "media", etiqueta: "Prioridad media" },
    { valor: "baja", etiqueta: "Prioridad baja" },
    { valor: "pasado", etiqueta: "Vuelo concluido" },
    { valor: "sin-hora", etiqueta: "Hora pendiente" },
  ];

  const formatearCoordinadores = (nombres) => {
    if (!nombres || nombres.length === 0) return "";
    if (nombres.length === 1) return nombres[0];
    if (nombres.length === 2) return `${nombres[0]} y ${nombres[1]}`;
    const ultimo = nombres[nombres.length - 1];
    return `${nombres.slice(0, -1).join(", ")} y ${ultimo}`;
  };

  useEffect(() => {
    fetchServicios();

    const intervalo = setInterval(() => {
      fetchServicios();
    }, 60000);

    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    servicios.forEach((serv) => {
      const minutos = moment(serv.horaVuelo).diff(moment(), "minutes");
      const claveToast = `toast-${serv.noServicio}`;

      if (minutos >= 0 && minutos <= 30 && !toast.isActive(claveToast)) {
        toast.warn(
          ({ closeToast }) => (
            <div>
              <strong>Servicio urgente</strong>
              <p>
                Vuelo <strong>{serv.noVuelo}</strong> — quedan <strong>{minutos} min</strong> para{" "}
                {serv.tipoService === "LLEGADAS" ? "aterrizar" : "abordar"}.
              </p>
              <button
                className="btn-asignar-toast"
                onClick={() => {
                  abrirModalAsignar(serv);
                  closeToast();
                }}
              >
                Asignar ahora
              </button>
            </div>
          ),
          {
            toastId: claveToast,
            autoClose: 10000,
            position: "top-right",
          }
        );
      }
    });
  }, [servicios]);

  useEffect(() => {
    if (!token || servicios.length === 0) return;

    const ids = Array.from(
      new Set(
        servicios
          .map((serv) => serv.usuarioInicio)
          .filter((id) => id !== null && id !== undefined && id !== "")
      )
    );

    const faltantes = ids.filter(
      (id) => !colaboradoresInfo[id] && !cargaColaboradoresRef.current.has(id)
    );

    if (!faltantes.length) return;

    const obtenerColaboradores = async () => {
      const nuevasEntradas = {};

      await Promise.all(
        faltantes.map(async (id) => {
          cargaColaboradoresRef.current.add(id);
          try {
            const { data } = await axios.get(`${API_BASE_URL}api/colaboradores/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            const nombre =
              data?.nombreCompleto ||
              [data?.nombre, data?.apellidoPaterno, data?.apellidoMaterno].filter(Boolean).join(" ") ||
              `Colaborador ${id}`;

            nuevasEntradas[id] = { nombre, datos: data };
          } catch (error) {
            nuevasEntradas[id] = { nombre: `Colaborador ${id}` };
          } finally {
            cargaColaboradoresRef.current.delete(id);
          }
        })
      );

      if (Object.keys(nuevasEntradas).length) {
        setColaboradoresInfo((prev) => ({
          ...prev,
          ...nuevasEntradas,
        }));
      }
    };

    obtenerColaboradores();
  }, [servicios, token, colaboradoresInfo]);

  const gruposFiltrados = useMemo(() => {
    const filtro = busquedaVuelo.trim().toUpperCase();
    const ahora = moment();

    const agrupados = servicios.reduce((acc, serv) => {
      const tipoServicio = (serv.tipoService || "").trim().toUpperCase();
      const filtraTipo = tiposActivos.length === 0 || tiposActivos.includes(tipoServicio);

      if (!filtraTipo) {
        return acc;
      }

      const vuelo = (serv.noVuelo || "SIN-VUELO").toString().toUpperCase();
      const horaReferencia = serv.horaVuelo || serv.horaInicio;
      const minutosHasta = horaReferencia ? moment(horaReferencia).diff(ahora, "minutes") : null;

      let urgenciaCalculada = "sin-hora";
      if (minutosHasta !== null) {
        if (minutosHasta < 0) urgenciaCalculada = "pasado";
        else if (minutosHasta <= 15) urgenciaCalculada = "alta";
        else if (minutosHasta <= 30) urgenciaCalculada = "media";
        else urgenciaCalculada = "baja";
      }
      serv.urgenciaNivelCached = urgenciaCalculada;

      if (filtro && !vuelo.includes(filtro)) {
        return acc;
      }
      if (filtroUrgencia !== "todos" && urgenciaCalculada !== filtroUrgencia) {
        return acc;
      }

      if (!acc[vuelo]) {
        acc[vuelo] = {
          vuelo,
          servicios: [],
          aerolinea: serv.aerolinea,
        };
      }

      acc[vuelo].servicios.push(serv);
      if (!acc[vuelo].aerolinea && serv.aerolinea) {
        acc[vuelo].aerolinea = serv.aerolinea;
      }

      return acc;
    }, {});

    const formateados = Object.values(agrupados).map((grupo) => {
      const serviciosOrdenados = [...grupo.servicios].sort((a, b) => {
        const horaA = a.horaVuelo || a.horaInicio;
        const horaB = b.horaVuelo || b.horaInicio;

        if (!horaA && !horaB) return 0;
        if (!horaA) return 1;
        if (!horaB) return -1;

        return moment(horaA).diff(moment(horaB));
      });

      const horaReferencia = serviciosOrdenados.map((serv) => serv.horaVuelo || serv.horaInicio).find(Boolean);
      const horaVueloMoment = horaReferencia ? moment(horaReferencia) : null;

      const tipoPrincipal = serviciosOrdenados.find((servicio) => servicio.tipoService)?.tipoService || "";

      const minutosValidos = serviciosOrdenados
        .map((servicio) => {
          const hora = servicio.horaVuelo || servicio.horaInicio;
          return hora ? moment(hora).diff(ahora, "minutes") : null;
        })
        .filter((min) => min !== null && !Number.isNaN(min));

      const minutosFuturos = minutosValidos.filter((min) => min >= 0);
      const minutosRestantes =
        minutosFuturos.length > 0
          ? Math.min(...minutosFuturos)
          : minutosValidos.length > 0
          ? Math.max(...minutosValidos)
          : null;

      const proximoFuturo = minutosFuturos.length > 0 ? Math.min(...minutosFuturos) : null;

      const esCritico = proximoFuturo !== null && proximoFuturo <= 30;

      const tiposServicio = Array.from(new Set(serviciosOrdenados.map((servicio) => servicio.tipoService).filter(Boolean)));

      const coordinadoresIds = Array.from(
        new Set(serviciosOrdenados.map((servicio) => servicio.usuarioInicio).filter((id) => id !== null && id !== undefined && id !== ""))
      );

      const coordinadoresNombres = coordinadoresIds.map((id) => {
        const info = colaboradoresInfo[id];
        return info?.nombre || `Colaborador ${id}`;
      });

      const fechaVueloTexto = horaVueloMoment ? horaVueloMoment.format("DD/MM/YYYY") : "Fecha N/D";
      const esHoy = horaVueloMoment ? horaVueloMoment.isSame(ahora, "day") : false;

      let tiempoRestanteTexto = "Hora de vuelo no registrada";
      let estadoTiempo = "sin-hora";
      let urgenciaNivel = "sin-hora";
      let urgenciaEtiqueta = "Hora pendiente";

      if (minutosRestantes !== null) {
        const diffMinutos = minutosRestantes;
        const diffAbsoluto = Math.abs(diffMinutos);
        const horas = Math.floor(diffAbsoluto / 60);
        const minutosPendientes = diffAbsoluto % 60;
        const partes = [];
        if (horas) partes.push(`${horas} h`);
        if (minutosPendientes || !partes.length) partes.push(`${minutosPendientes} min`);

        const accionFuturo = tipoPrincipal === "LLEGADAS" ? "aterrizar" : "abordar";
        const accionPasado = tipoPrincipal === "LLEGADAS" ? "el aterrizaje" : "el abordaje";

        if (diffMinutos > 0) {
          tiempoRestanteTexto = `Faltan ${partes.join(" ")} para ${accionFuturo}`;
          estadoTiempo = "futuro";
        } else if (diffMinutos === 0) {
          tiempoRestanteTexto = `Es momento de ${accionFuturo}`;
          estadoTiempo = "presente";
        } else {
          tiempoRestanteTexto = `Han pasado ${partes.join(" ")} desde ${accionPasado}`;
          estadoTiempo = "pasado";
        }

        if (proximoFuturo !== null) {
          if (proximoFuturo <= 15) {
            urgenciaNivel = "alta";
            urgenciaEtiqueta = "Prioridad alta";
          } else if (proximoFuturo <= 30) {
            urgenciaNivel = "media";
            urgenciaEtiqueta = "Prioridad media";
          } else {
            urgenciaNivel = "baja";
            urgenciaEtiqueta = "Prioridad baja";
          }
        } else if (diffMinutos < 0) {
          urgenciaNivel = "pasado";
          urgenciaEtiqueta = "Vuelo concluido";
        }
      }

      return {
        ...grupo,
        tieneAmApi: serviciosOrdenados.some((srv) => srv.source === 'AM_API'),
        servicios: serviciosOrdenados,
        horaVueloMoment,
        horaVueloTexto: horaVueloMoment ? horaVueloMoment.format("HH:mm") : "-",
        esCritico,
        resumenTipos: tiposServicio.length ? tiposServicio.join(" • ") : "Tipo no disponible",
        fechaVueloTexto,
        esHoy,
        tiempoRestanteTexto,
        estadoTiempo,
        minutosRestantes,
        urgenciaNivel,
        urgenciaEtiqueta,
        coordinadoresIds,
        coordinadoresNombres,
      };
    });

    return formateados.sort((a, b) => {
      if (a.horaVueloMoment && b.horaVueloMoment) {
        return a.horaVueloMoment.diff(b.horaVueloMoment);
      }
      if (a.horaVueloMoment) return -1;
      if (b.horaVueloMoment) return 1;
      return a.vuelo.localeCompare(b.vuelo);
    });
  }, [busquedaVuelo, servicios, colaboradoresInfo, filtroUrgencia, tiposActivos]);

  const totalServicios = useMemo(
    () => gruposFiltrados.reduce((acc, grupo) => acc + grupo.servicios.length, 0),
    [gruposFiltrados]
  );

  const timelineEventos = useMemo(
    () =>
      gruposFiltrados.map((grupo) => ({
        vuelo: grupo.vuelo,
        aerolinea: grupo.aerolinea,
        horaTexto: grupo.horaVueloTexto,
        fechaTexto: grupo.fechaVueloTexto,
        resumenTipos: grupo.resumenTipos,
        tiempoRestanteTexto: grupo.tiempoRestanteTexto,
        estadoTiempo: grupo.estadoTiempo,
        urgenciaNivel: grupo.urgenciaNivel,
        urgenciaEtiqueta: grupo.urgenciaEtiqueta,
        esHoy: grupo.esHoy,
        minutosRestantes: grupo.minutosRestantes,
        servicios: grupo.servicios.length,
      })),
    [gruposFiltrados]
  );

  const alertasCriticas = useMemo(
    () =>
      timelineEventos
        .filter(
          (evento) =>
            evento.minutosRestantes !== null &&
            evento.minutosRestantes >= 0 &&
            evento.minutosRestantes <= 30
        )
        .sort((a, b) => a.minutosRestantes - b.minutosRestantes),
    [timelineEventos]
  );

  const fetchServicios = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}api/servicios/serviciosStatus?status=REGISTRADO&incluirWCHC=1&excluirAerolineas=DL`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const norm = (s) => (s || "").trim().toUpperCase();
      const estacionToken = estacionUsuario ? norm(estacionUsuario) : null;
      const getEstacionServicio = (s) => norm(s?.estacion ?? s?.Estacion ?? s?.station ?? s?.stationCode);

      const data = Array.isArray(res.data) ? res.data : [];
      const filtrados = estacionToken ? data.filter((s) => getEstacionServicio(s) === estacionToken) : data;

      const ordenados = filtrados.sort((a, b) => {
        const horaA = a.horaVuelo || a.horaInicio;
        const horaB = b.horaVuelo || b.horaInicio;
        return moment(horaA).diff(moment(horaB));
      });
      setServicios(ordenados);
    } catch (error) {
      console.error("Error al obtener servicios:", error);
    } finally {
      setLoadingPage(false);
    }
  };

  const abrirModalAsignar = async (servicio) => {
    try {
      const res = await axios.get(`${API_BASE_URL}api/bitacora/en-turno-detalles`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const disponibles = res.data.filter(
        (c) => c.estacion?.trim().toUpperCase() === estacionUsuario
      );

      setColaboradores(disponibles);
      setServicioSeleccionado(servicio);
      setModalVisible(true);
    } catch (error) {
      toast.error("Error al obtener colaboradores");
    }
  };

  const asignarColaborador = async (colab) => {
    if (asignando) return;
    setAsignando(true);
    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/msg/${servicioSeleccionado._id}/${servicioSeleccionado.tipoService}/${servicioSeleccionado.aerolinea}/${servicioSeleccionado.noVuelo}`,
        {
          noColaborador: colab.noColaborador,
          statusServicio: "ASIGNADO",
          USUARIO: decoded?.usuario,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("✅ Servicio asignado");
      setModalVisible(false);
      setServicioSeleccionado(null);
      fetchServicios();
    } catch (err) {
      toast.error("❌ Error al asignar servicio");
    } finally {
      setAsignando(false);
    }
  };

  const marcarNoShow = async (servicio) => {
    const servicioId = servicio._id || servicio.noServicio;

    if (!servicioId) {
      toast.error("No se pudo identificar el servicio.");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}api/servicios/${servicioId}`,
        {
          statusServicio: "NOSHOW",
          horaFin: moment().tz("America/Mexico_City").format("YYYY-MM-DD HH:mm:ss"),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.info("🚫 Servicio marcado como NOSHOW");
      fetchServicios();
    } catch (error) {
      console.error("Error al marcar NOSHOW:", error);
      toast.error("❌ No se pudo actualizar a NOSHOW");
    }
  };

  if (loadingPage) {
    return (
      <div className="servicios-registrados-page">
        <div className="page-loading-overlay">
          <div className="page-loading-spinner" />
          <p className="page-loading-text">Cargando servicios registrados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="servicios-registrados-page">
      <ToastContainer
        position="top-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <h1>Servicios Registrados</h1>

      <div className="controles-superiores">
        <div className="buscador-vuelo">
          <label htmlFor="filtro-vuelo">Buscar por número de vuelo</label>
          <input
            id="filtro-vuelo"
            type="text"
            placeholder="Ej. AM1987"
            value={busquedaVuelo}
            onChange={(e) => setBusquedaVuelo(e.target.value.toUpperCase())}
          />
        </div>
        <div className="filtros-urgencia">
          {opcionesUrgencia.map((opcion) => (
            <button
              key={opcion.valor}
              type="button"
              className={`btn-filtro-urgencia ${filtroUrgencia === opcion.valor ? "activo" : ""} ${opcion.valor}`}
              onClick={() =>
                setFiltroUrgencia((prev) =>
                  opcion.valor === "todos" ? "todos" : prev === opcion.valor ? "todos" : opcion.valor
                )
              }
            >
              {opcion.etiqueta}
            </button>
          ))}
        </div>
        <div className="filtros-tipo">
          <span className="filtros-tipo-label">Tipo de vuelo</span>
          <button
            type="button"
            className={`btn-filtro-tipo llegadas ${filtroTipos.LLEGADAS ? "activo" : ""}`}
            onClick={() => setFiltroTipos((prev) => ({ ...prev, LLEGADAS: !prev.LLEGADAS }))}
          >
            Llegadas
          </button>
          <button
            type="button"
            className={`btn-filtro-tipo salidas ${filtroTipos.SALIDAS ? "activo" : ""}`}
            onClick={() => setFiltroTipos((prev) => ({ ...prev, SALIDAS: !prev.SALIDAS }))}
          >
            Salidas
          </button>
        </div>
        <div className="resumen-servicios">
          <span>{gruposFiltrados.length} vuelos</span>
          <span>{totalServicios} servicios</span>
        </div>
      </div>

      <div className="layout-servicios">
        <div className="panel-principal">
          <div className="scroll-wrapper">
            {gruposFiltrados.length === 0 ? (
              <div className="sin-resultados">
                <p>No hay servicios registrados para ese vuelo.</p>
              </div>
            ) : (
              <div className="contenedor-grupos">
                {gruposFiltrados.length > 0 && gruposFiltrados.some((g) => g.tieneAmApi) && (
                  <div className='am-api-section-header' style={{
                    background: 'linear-gradient(90deg,#fef3c7,#fde68a)',
                    border: '1px solid #f59e0b',
                    borderRadius: 10,
                    padding: '10px 14px',
                    margin: '8px 0 10px',
                    fontWeight: 700,
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <span style={{fontSize:18}}>🛬</span>
                    Servicios desde API Aeroméxico
                  </div>
                )}
                {[...gruposFiltrados].sort((a,b) => (b.tieneAmApi?1:0) - (a.tieneAmApi?1:0)).map((grupo, idxGrp, arr) => (
                  <React.Fragment key={grupo.vuelo + '-' + idxGrp}>
                    {!grupo.tieneAmApi && (idxGrp === 0 || arr[idxGrp - 1].tieneAmApi) && (
                      <div className='am-api-separator' style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        margin: '20px 0 14px',
                      }}>
                        <div style={{flex:1, height:1, background:'#e5e7eb'}}/>
                        <span style={{fontSize:12, color:'#64748b', letterSpacing:'0.08em', textTransform:'uppercase'}}>Resto de servicios</span>
                        <div style={{flex:1, height:1, background:'#e5e7eb'}}/>
                      </div>
                    )}
                    
                  <section key={grupo.vuelo} className={`tarjeta-grupo ${grupo.esCritico ? "grupo-critico" : ""}`}>
                    <header className="tarjeta-encabezado">
                      <div className="tarjeta-descripcion">
                        <h2>{grupo.vuelo}</h2>
                        <p>
                          {grupo.aerolinea || "Aerolinea N/D"} • {grupo.resumenTipos} • {grupo.vuelo}
                        </p>
                        <p className={`detalle-fecha ${grupo.esHoy ? "fecha-hoy" : "fecha-otro-dia"}`}>
                          {grupo.fechaVueloTexto} {grupo.esHoy ? "(Hoy)" : ""}
                        </p>
                        <p className={`tiempo-restante ${grupo.estadoTiempo}`}>{grupo.tiempoRestanteTexto}</p>
                      </div>
                      <div className="tarjeta-metadata">
                        <span className="tag">{grupo.horaVueloTexto}</span>
                        <span className={`tag tag-urgencia ${grupo.urgenciaNivel}`}>{grupo.urgenciaEtiqueta}</span>
                        <span className={`tag ${grupo.esHoy ? "tag-hoy" : "tag-alerta"}`}>
                          {grupo.esHoy ? "Servicio del día" : "Fecha distinta"}
                        </span>
                        <span className="tag secundario">
                          {grupo.servicios.length} {grupo.servicios.length === 1 ? "servicio" : "servicios"}
                        </span>
                      </div>
                    </header>

                    <table className="tabla-reg grupo">
                      <thead>
                        <tr>
                          <th>Pasajero</th>
                          <th>Asiento</th>
                          <th>Sala</th>
                          <th>Mostrador</th>
                          <th>Kiosk</th>
                          <th>Hora registro</th>
                          <th>Conexión</th>
                          <th>Tipo silla</th>
                          <th>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.servicios.map((serv) => {
                          const minutosRestantes = serv.horaVuelo ? moment(serv.horaVuelo).diff(moment(), "minutes") : null;
                          const esCritico = minutosRestantes !== null && minutosRestantes >= 0 && minutosRestantes <= 30;

                          let urgenciaFila = "sin-hora";
                          if (minutosRestantes !== null) {
                            if (minutosRestantes < 0) urgenciaFila = "pasado";
                            else if (minutosRestantes <= 15) urgenciaFila = "alta";
                            else if (minutosRestantes <= 30) urgenciaFila = "media";
                            else urgenciaFila = "baja";
                          }

                          const clasesFila = ["fila-servicio", `fila-urgencia-${urgenciaFila}`, esCritico ? "fila-alerta-urgente zoom" : ""]
                            .filter(Boolean)
                            .join(" ");

                          let tituloFila = "Hora de vuelo no registrada";
                          if (minutosRestantes !== null) {
                            const minutosAbsolutos = Math.abs(minutosRestantes);
                            if (minutosRestantes >= 0) {
                              tituloFila = `Faltan ${minutosAbsolutos} minutos`;
                            } else {
                              tituloFila = `Han pasado ${minutosAbsolutos} minutos`;
                            }
                          }

                          return (
                            <tr
                              key={serv.noServicio}
                              className={clasesFila}
                              onAnimationEnd={(e) => e.currentTarget.classList.remove("zoom")}
                              title={tituloFila}
                            >
                              <td>
                                <strong>{serv.nombrePax}</strong>
                                <span className="detalle-servicio">
                                  {serv.noVuelo} • {serv.aerolinea}
                                </span>
                              </td>
                              <td>{serv.noAsiento || "—"}</td>
                              <td>{serv.sala || "—"}</td>
                              <td>{serv.noMostrador || "—"}</td>
                              <td>{serv.kioskId || serv.externalPayload?.kioskId || serv.numberKiosk || serv.externalPayload?.numberKiosk || "—"}</td>
                              <td>{moment(serv.horaInicio).format("HH:mm")}</td>
                              <td>{serv.conexion || "—"}</td>
                              <td>{serv.tipoSilla || "—"}</td>
                              <td>
                                <div className="acciones-tabla">
                                  <button className="btn-asignar" onClick={() => abrirModalAsignar(serv)}>
                                    Asignar
                                  </button>
                                  {(serv.tipoSilla || "").toUpperCase().includes("WCHC") && (
                                    <button
                                      className="btn-apoyo-vuelo"
                                      onClick={() => setApoyoServicio(serv)}
                                      title="Solicitar operadores de apoyo (WCHC / pasillera)"
                                    >
                                      Apoyos
                                    </button>
                                  )}
                                  <button className="btn-noshow" onClick={() => marcarNoShow(serv)}>
                                    NOSHOW
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {grupo.urgenciaNivel === "pasado" && grupo.coordinadoresNombres.length > 0 && (
                      <div className="aviso-coordinador">
                        {grupo.coordinadoresNombres.length > 1 ? "Los coordinadores" : "El coordinador"} {formatearCoordinadores(grupo.coordinadoresNombres)}{" "}
                        {grupo.coordinadoresNombres.length > 1 ? "tienen" : "tiene"} que cerrar este servicio, favor de atender.
                      </div>
                    )}
                  </section>
                </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="panel-lateral">
          <section className="tarjeta-lateral legend">
            <h3>Guía de colores</h3>
            <ul className="legend-lista">
              <li>
                <span className="legend-dot alta" />
                Prioridad alta (≤ 15 min)
              </li>
              <li>
                <span className="legend-dot media" />
                Prioridad media (16 - 30 min)
              </li>
              <li>
                <span className="legend-dot baja" />
                Prioridad baja (+ 30 min)
              </li>
              <li>
                <span className="legend-dot pasado" />
                Vuelo concluido
              </li>
              <li>
                <span className="legend-dot sin-hora" />
                Hora pendiente / sin definir
              </li>
            </ul>
          </section>
          <section className="tarjeta-lateral timeline">
            <h3>Próximos vuelos</h3>
            {timelineEventos.length ? (
              <ul className="timeline-lista">
                {timelineEventos.map((evento) => (
                  <li key={evento.vuelo} className={`timeline-item ${evento.estadoTiempo} ${evento.urgenciaNivel}`}>
                    <div className="timeline-icono" />
                    <div className="timeline-contenido">
                      <div className="timeline-encabezado">
                        <span className="timeline-hora">{evento.horaTexto}</span>
                        <span className="timeline-vuelo">{evento.vuelo}</span>
                      </div>
                      <p className="timeline-detalle">
                        {evento.aerolinea || "Aerolinea N/D"} • {evento.resumenTipos}
                      </p>
                      <p className="timeline-tiempo">{evento.tiempoRestanteTexto}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="texto-vacio">Sin vuelos registrados.</p>
            )}
          </section>

          <section className="tarjeta-lateral alertas">
            <h3>Alertas críticas</h3>
            {alertasCriticas.length ? (
              <ul className="alertas-lista">
                {alertasCriticas.map((alerta) => (
                  <li key={`${alerta.vuelo}-alerta`} className="alerta-item">
                    <span className="alerta-hora">{alerta.horaTexto}</span>
                    <div className="alerta-detalle">
                      <strong>{alerta.vuelo}</strong>
                      <p>{alerta.tiempoRestanteTexto}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="texto-vacio">No hay vuelos críticos en los próximos 30 min.</p>
            )}
          </section>
        </aside>
      </div>

      {modalVisible && (
        <div className="modal-overlay-pasajeros" onClick={() => setModalVisible(false)}>
          <div className="modal-pasajeros" onClick={(e) => e.stopPropagation()}>
            <h3>Selecciona colaborador</h3>
            <div className="tabla-scroll">
              <input
                type="text"
                placeholder="Buscar por siglas..."
                value={busquedaSiglas}
                onChange={(e) => setBusquedaSiglas(e.target.value.toUpperCase())}
                className="input-busqueda"
              />

              <table className="tabla-pasajeros">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Siglas</th>
                    <th>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradores
                    .filter((c) => c.siglas.toUpperCase().includes(busquedaSiglas))
                    .map((c, idx) => (
                      <tr key={c.noColaborador}>
                        <td>{idx + 1}</td>
                        <td>{c.nombreCompleto}</td>
                        <td>{c.siglas}</td>
                        <td>
                          <button className="btn-asignar" onClick={() => asignarColaborador(c)} disabled={asignando}>
                            {asignando ? "Asignando..." : "Seleccionar"}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="text-end">
              <button className="btn-cerrar" onClick={() => setModalVisible(false)}>
                ✖ Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <ModalSolicitarApoyo
        servicio={apoyoServicio}
        onClose={() => setApoyoServicio(null)}
      />
    </div>
  );
};

export default ServiciosRegistradosPage;
